import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material';
import { InterrogationMemory, OntologyEntity, OntologyRelation } from '../utils/backend-utils';
import { ENTITY_CATEGORIES, categoryColor } from '../utils/ontologyConstants';
import {
  buildGraphData,
  matchSearch,
  nodesWithinHops,
} from '../utils/graphHopUtils';
import { KnowledgeGraphD3 } from './KnowledgeGraphD3';

interface KnowledgeGraphPanelProps {
  open: boolean;
  onClose: () => void;
  memory?: InterrogationMemory;
  consolidationStatus?: 'none' | 'pending' | 'running' | 'complete' | 'failed';
}

function getEntitiesFromMemory(memory?: InterrogationMemory): OntologyEntity[] {
  if (memory?.entities && memory.entities.length > 0) {
    return memory.entities;
  }
  return (memory?.entity_registry || []).map((row) => ({
    id: `other:${row.name.toLowerCase().replace(/\s+/g, '-')}`,
    name: row.name,
    category: 'Other',
    subtype: row.type,
    notes: row.notes,
    aliases: [],
  }));
}

export function KnowledgeGraphPanel({ open, onClose, memory, consolidationStatus = 'none' }: KnowledgeGraphPanelProps) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [focusNodeId, setFocusNodeId] = useState<string | null>(null);
  const [hopCount, setHopCount] = useState(2);
  const [enabledCategories, setEnabledCategories] = useState<Set<string>>(
    () => new Set(ENTITY_CATEGORIES)
  );

  const entities = useMemo(() => getEntitiesFromMemory(memory), [memory]);
  const relations: OntologyRelation[] = useMemo(
    () => memory?.relations || [],
    [memory?.relations]
  );

  const { nodes: allNodes, links: allLinks } = useMemo(
    () => buildGraphData(entities, relations),
    [entities, relations]
  );

  const filteredNodes = useMemo(() => {
    if (enabledCategories.size === ENTITY_CATEGORIES.length) return allNodes;
    return allNodes.filter((n) => enabledCategories.has(n.category || 'Other'));
  }, [allNodes, enabledCategories]);

  const filteredNodeIds = useMemo(
    () => new Set(filteredNodes.map((n) => n.id)),
    [filteredNodes]
  );

  const filteredLinks = useMemo(
    () => allLinks.filter((l) => filteredNodeIds.has(l.source) && filteredNodeIds.has(l.target)),
    [allLinks, filteredNodeIds]
  );

  const hopVisibleIds = useMemo(() => {
    const focus = focusNodeId || selectedNodeId;
    return nodesWithinHops(focus, filteredLinks, hopCount);
  }, [focusNodeId, selectedNodeId, filteredLinks, hopCount]);

  const searchMatchIds = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return undefined;
    return new Set(filteredNodes.filter((n) => matchSearch(n, q)).map((n) => n.id));
  }, [searchQuery, filteredNodes]);

  const dimmedNodeIds = useMemo(() => {
    if (hopVisibleIds) return hopVisibleIds;
    return undefined;
  }, [hopVisibleIds]);

  const selectedNode = useMemo(
    () => allNodes.find((n) => n.id === selectedNodeId) || null,
    [allNodes, selectedNodeId]
  );

  const connectedRelations = useMemo(() => {
    if (!selectedNodeId) return [];
    return allLinks.filter((l) => l.source === selectedNodeId || l.target === selectedNodeId);
  }, [allLinks, selectedNodeId]);

  const toggleCategory = (cat: string) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '80vw',
          height: '80vh',
          maxWidth: 'none',
          maxHeight: 'none',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
          Knowledge graph
        </Typography>
        {(consolidationStatus === 'pending' || consolidationStatus === 'running') && (
          <Chip
            size="small"
            label="Enriching knowledge graph…"
            color="info"
            variant="outlined"
          />
        )}
        <Typography variant="caption" color="text.secondary">
          {filteredNodes.length} nodes · {filteredLinks.length} edges
        </Typography>
        <IconButton onClick={onClose} aria-label="Close knowledge graph">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            placeholder="Search nodes…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Hops:
            </Typography>
            {[1, 2, 3].map((h) => (
              <Chip
                key={h}
                size="small"
                label={String(h)}
                color={(focusNodeId || selectedNodeId) && hopCount === h ? 'primary' : 'default'}
                variant={(focusNodeId || selectedNodeId) && hopCount === h ? 'filled' : 'outlined'}
                onClick={() => setHopCount(h)}
              />
            ))}
            {(focusNodeId || selectedNodeId) && (
              <Chip
                size="small"
                label="Clear focus"
                variant="outlined"
                onClick={() => {
                  setFocusNodeId(null);
                  setSelectedNodeId(null);
                }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              width: 200,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              p: 1.5,
              overflowY: 'auto',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Categories
            </Typography>
            <FormGroup sx={{ mt: 0.5 }}>
              {ENTITY_CATEGORIES.map((cat) => (
                <FormControlLabel
                  key={cat}
                  control={
                    <Checkbox
                      size="small"
                      checked={enabledCategories.has(cat)}
                      onChange={() => toggleCategory(cat)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: categoryColor(cat),
                        }}
                      />
                      <Typography variant="body2">{cat}</Typography>
                    </Box>
                  }
                  sx={{ ml: 0, mr: 0 }}
                />
              ))}
            </FormGroup>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            {filteredNodes.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                }}
              >
                <Typography color="text.secondary" textAlign="center">
                  No entities extracted yet. Keep talking — the graph will populate as the session
                  progresses.
                </Typography>
              </Box>
            ) : (
              <KnowledgeGraphD3
                nodes={filteredNodes}
                links={filteredLinks}
                selectedNodeId={selectedNodeId}
                onSelectNode={(id) => {
                  setSelectedNodeId(id);
                  if (id) setFocusNodeId(id);
                }}
                dimmedNodeIds={dimmedNodeIds}
                searchMatchIds={searchMatchIds}
              />
            )}
          </Box>

          <Box
            sx={{
              width: 260,
              flexShrink: 0,
              borderLeft: 1,
              borderColor: 'divider',
              p: 1.5,
              overflowY: 'auto',
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Details
            </Typography>
            {!selectedNode ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click a node to inspect relations and evidence.
              </Typography>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {selectedNode.name}
                </Typography>
                <Chip
                  size="small"
                  label={selectedNode.category}
                  sx={{ mt: 0.5, bgcolor: alpha(categoryColor(selectedNode.category), 0.15) }}
                />
                {selectedNode.subtype && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedNode.subtype}
                  </Typography>
                )}
                {selectedNode.notes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {selectedNode.notes}
                  </Typography>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">
                  Relations ({connectedRelations.length})
                </Typography>
                {connectedRelations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    None yet
                  </Typography>
                ) : (
                  connectedRelations.slice(0, 12).map((rel) => {
                    const other =
                      rel.source === selectedNodeId ? rel.target : rel.source;
                    const dir = rel.source === selectedNodeId ? '→' : '←';
                    return (
                      <Box key={rel.id} sx={{ mt: 0.75 }}>
                        <Typography variant="body2">
                          {dir} {rel.predicate} {other}
                        </Typography>
                        {rel.evidence && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            “{rel.evidence}”
                          </Typography>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            )}

            {memory?.rolling_summary && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">
                  Rolling summary
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {memory.rolling_summary}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
